import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { CarCard } from "@/components/site/CarCard";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { InnerPageRequirements } from "@/components/site/InnerPageRequirements";
import { ReviewsHighlight } from "@/components/site/ReviewsHighlight";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cars } from "@/lib/cars";
import { Phone } from "lucide-react";
import { breadcrumbScript } from "@/lib/schema";

const exoticFaqs = [
  {
    q: "What actually separates 'exotic' from 'luxury' in the Avolus fleet?",
    a: "Luxury is defined by comfort and finish: the Mercedes-Benz S-Class or BMW X7 M60i, cars built to insulate you from the road. Exotic is defined by performance and engagement: the BMW M3 Competition and BMW M440i are set up to give you feedback through the wheel, throttle, and seat. Both tiers have the same $2,000 refundable security deposit and $60/day LDW; performance rentals retain their vehicle-specific mileage rules and require renters to be 25 or older with a clean full-class license.",
  },
  {
    q: "Which driving roads should I plan for?",
    a: "Three routes make Vancouver exceptional for an exotic rental. The Sea-to-Sky Highway (Highway 99 from Horseshoe Bay to Whistler) is 120 km of coastal sweepers, the reason most of our exotic bookings exist. The Stanley Park Causeway and seawall loop is a 20-minute low-speed showcase drive perfect for photography and a first-time feel of the car. And the Duffey Lake Road, north of Pemberton, is empty, technical, and unforgettable, worth a full-day booking and a fuel stop in Whistler. For a shorter after-work blast, Cypress Mountain and Mount Seymour both offer tight switchbacks 15 minutes from downtown.",
  },
  {
    q: "How is insurance different on a performance car?",
    a: "The $5,000,000 third-party liability floor is identical across our fleet. Loss Damage Waiver (LDW) is $60 per day on every vehicle and limits the renter's financial responsibility subject to the deductible and terms in the rental agreement. Track use is never covered under a standard rental.",
  },
  {
    q: "How much is the security deposit on an exotic?",
    a: "The refundable security deposit is $2,000 CAD on every vehicle. It is pre-authorized on a major credit card in the renter's name at delivery and released after inspection when the car is returned in the same condition. Debit, prepaid, or third-party cards cannot hold the deposit.",
  },
  {
    q: "Why is the age minimum 25 and why so strict on license class?",
    a: "It is an insurance-underwriter requirement, not a company preference: our third-party liability coverage on performance vehicles is priced on a driver pool of 25-plus with a full unrestricted license and clean recent history. Novice (N) and Learner (L) BC classes, and equivalent restricted classes from other provinces or countries, are not eligible under the policy. We verify age and license class at pickup with government photo ID; there is no workaround by naming a 25+ additional driver if the person who plans to drive is under 25.",
  },
  {
    q: "Is there a mileage limit on exotic rentals?",
    a: "Daily rentals include 150 km per day. Additional kilometres on the BMW M3 Competition and BMW M440i cost $1.50/km. Check your selected vehicle’s rate card and rental agreement for mileage allowances, and contact us before booking if you expect a longer drive.",
  },
  {
    q: "Can I take an exotic to a track day at Vancouver Island Motorsport Circuit or Mission Raceway?",
    a: "No. Track use, autocross, timed events, and any closed-course driving are prohibited and void the insurance policy and LDW on every vehicle in the fleet.",
  },
  {
    q: "Do the exotic cars have GPS tracking?",
    a: "Yes, every vehicle in the fleet has GPS. We do not monitor everyday driving. The data is only reviewed if the car comes back late, is reported missing, or if there's a specific concern like heavy speeding, off-road driving, or track use. Normal spirited driving on the Sea-to-Sky is exactly what these cars are for and is not flagged.",
  },
];

const performance = cars.filter((c) =>
  ["bmw-m3-2023", "bmw-m440-2021", "porsche-macan-2023", "mercedes-s-class-2022"].includes(c.slug),
);

export const Route = createFileRoute("/exotic-car-rental-vancouver")({
  component: ExoticLanding,
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Exotic Car Rental Vancouver | M3, Macan & S580 | Avolus Cars" },
      { name: "description", content: "Vancouver exotic car rental: BMW M3 Competition, M440i, Porsche Macan, Mercedes S580. Built for the Sea-to-Sky drive." },
      { property: "og:title", content: "Exotic Car Rental Vancouver | M3, Macan & S580 | Avolus Cars" },
      { property: "og:description", content: "Performance and exotic cars for the Sea-to-Sky, Cypress, and Duffey Lake, delivered across Metro Vancouver." },
      { property: "og:url", content: "https://avoluscars.com/exotic-car-rental-vancouver" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/exotic-car-rental-vancouver" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Exotic Car Rental",
          provider: { "@id": "https://avoluscars.com/#business" },
          areaServed: { "@type": "City", name: "Vancouver, British Columbia" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: exoticFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Exotic Car Rental Vancouver", url: "https://avoluscars.com/exotic-car-rental-vancouver" },
      ]),
    ],
  }),
});

function ExoticLanding() {
  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Vancouver Exotic Car Rental</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              Exotic Car Rental Vancouver
            </h1>
            <nav aria-label="On this page" className="md:hidden mt-4 flex flex-wrap gap-x-5 text-sm underline underline-offset-4"><a href="#vehicles" className="inline-flex items-center min-h-11">Vehicles</a><a href="#questions" className="inline-flex items-center min-h-11">Questions</a><a href="/rates" className="inline-flex items-center min-h-11">Rates</a></nav>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Exotic and performance rentals for the Sea-to-Sky. Our exotic car rental in Vancouver focuses on luxury and performance vehicles, not supercars: BMW M3 Competition, M440i, Porsche Macan, and Mercedes-Benz S580, cars
              built to be driven on the roads Vancouver was built for. Every vehicle has the
              same $2,000 refundable deposit and mandatory $60/day LDW, with renters required
              to be 25 or older and hold a full license.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="/book"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Book Now
              </a>
              <a
                href="tel:+17783798881"
                className="md:hidden inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Phone size={14} /> 778-379-8881
              </a>
              <a
                href="/contact"
                className="hidden md:inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Contact Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Exotic vs Luxury</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              What actually separates the two.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Some days you want the drive to disappear. That's what the{" "}
                <Link to="/fleet/$slug" params={{ slug: "mercedes-s-class-2022" }} className="underline underline-offset-4 hover:text-foreground">Mercedes-Benz S580 luxury rental</Link>{" "}
                and{" "}
                <Link to="/fleet/$slug" params={{ slug: "bmw-x7-m60i-2024" }} className="underline underline-offset-4 hover:text-foreground">BMW X7 M60i luxury SUV</Link>{" "}
                are for. They glide over potholes, keep the cabin quiet, and let you
                walk into a meeting or dinner feeling fresh instead of frazzled.
              </p>
              <p>
                Other days the drive is the whole point. The{" "}
                <Link to="/fleet/$slug" params={{ slug: "bmw-m3-2023" }} className="underline underline-offset-4 hover:text-foreground">BMW M3 Competition performance rental</Link>{" "}
                and{" "}
                <Link to="/fleet/$slug" params={{ slug: "bmw-m440-2021" }} className="underline underline-offset-4 hover:text-foreground">BMW M440i sport coupe</Link>{" "}
                respond the moment you touch the throttle: the exhaust note, the
                steering, the way the car leans into a corner. If the S580 is built
                for arriving, the M3 is built for the road between here and Sea-to-Sky.
              </p>
              <p>
                Whichever way you lean, the experience stays the same: $5,000,000
                third-party liability, unbranded vehicles, free{" "}
                <Link to="/delivery" className="underline underline-offset-4 hover:text-foreground">delivery inside Vancouver</Link>,
                concierge 8 AM - 8 PM daily, a $2,000 refundable deposit, and $60/day LDW on every
                vehicle. The only real difference between the two is which one puts
                the smile on your face.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>The Driving Roads</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Where these cars belong.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                <strong className="text-foreground">Sea-to-Sky Highway (Hwy 99, Horseshoe Bay to Whistler).</strong>
                {" "}120 km of coastal sweepers with ocean on one side and granite on the
                other. Enough elevation change and radius variety to genuinely exercise a
                performance car. Best done mid-morning on a weekday to avoid weekend Whistler
                traffic. Plan for a fuel and coffee stop in Squamish at the halfway point.
              </p>
              <p>
                <strong className="text-foreground">Stanley Park Causeway and seawall loop.</strong>
                {" "}A 20-minute low-speed showcase drive that puts the car against the
                Vancouver skyline, Lions Gate Bridge, and English Bay. Perfect for
                photography and a first-time feel of the vehicle before you commit to a
                longer route.
              </p>
              <p>
                <strong className="text-foreground">Duffey Lake Road (Hwy 99 north of Pemberton).</strong>
                {" "}Empty, technical, and one of the most rewarding drives in the province.
                Not a half-day trip. Plan a full 12-hour rental, fuel up in Whistler on the
                way north, and take the loop back through the coast range slowly. Cell
                service is patchy; download offline maps.
              </p>
              <p>
                <strong className="text-foreground">Cypress Mountain &amp; Mount Seymour.</strong>
                {" "}Tight switchbacks 15 minutes from downtown. Ideal for a two-hour
                after-work drive in the M3 or M440i without committing to a full Sea-to-Sky
                day.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Insurance &amp; Deposits</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              What performance cars change.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Every rental starts with $5,000,000 of third-party liability coverage. Loss
                Damage Waiver (LDW) is $60 per day on every vehicle and limits
                the renter's financial responsibility subject to the deductible and terms in
                the signed rental agreement.
              </p>
              <p>
                The refundable security deposit is $2,000 on every vehicle. It is pre-authorized
                on a major credit card in the renter's name at delivery and released after the
                return inspection. Track use, autocross, and closed-course driving void the policy
                and LDW on every vehicle.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Age Policy</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              25+ with a full license, no exceptions.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Every driver on an exotic rental, primary and additional, must be at least
                25 years old and hold a full, unrestricted license. Novice (N) and Learner
                (L) BC classes, and equivalent restricted classes from other provinces or
                countries, are not eligible. This is an insurance-underwriter requirement,
                not a company preference: our third-party liability coverage on performance
                vehicles is priced on a driver pool of 25+ with clean, unrestricted licenses
                and no major recent claims history.
              </p>
              <p>
                We verify at pickup with government photo ID and the physical license. There
                is no workaround. Naming a 25+ additional driver does not qualify a car for
                a younger driver, and we do check the vehicle telematics.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="vehicles" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Performance Fleet</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground max-w-2xl mb-12">
              Built for the drive.
            </h2>
          </Reveal>
          <div className="compact-fleet grid gap-8 md:grid-cols-2">
            {performance.map((c, i) => (
              <CarCard key={c.slug} car={c} index={i} compact />
            ))}
          </div>
          <div className="mt-10">
            <Link to="/fleet" className="text-xs uppercase tracking-[0.25em] text-foreground border-b border-foreground/40 hover:border-foreground pb-1">
              View entire fleet →
            </Link>
          </div>
        </div>
      </section>

      <section id="questions" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Exotics FAQ</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-10">
              Performance-car specifics.
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {exoticFaqs.map((f, i) => (
                <AccordionItem key={i} value={`e-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-base md:text-lg font-display text-foreground py-6 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal>
            <p className="mt-10 text-sm text-muted-foreground">
              Planning a long weekend on the Duffey? <Link to="/contact" className="text-foreground underline underline-offset-4">Talk to the concierge</Link>. We quote higher-mileage arrangements directly.
            </p>
          </Reveal>
        </div>
      </section>

      <ReviewsHighlight eyebrow="Performance Rental Reviews" />
      <InnerPageRequirements />
    </Layout>
  );
}
